<template>
  <form class="login-form" @submit.prevent="onSubmit">
    <slot name="header"></slot>
    <div class="login-form__content column">
      <input-text
        v-model:value="data.emailValue"
        :label="$t('labels.email')"
        autocomplete="username"
        type="email"
        name="email"
        :hide-errors="state.hideErrors"
        :autofocus="true"
        :required="true"
      />

      <input-password
        v-model:value="data.password"
        :label="$t('labels.password')"
        autocomplete="current-password"
        name="password"
        :hide-errors="state.hideErrors"
        :required="true"
      >
        <template #top-right>
          <span class="login-form__forgotpasswordlink">
            <slot name="forgot-password">
              <a>{{ $t("labels.forgotPassword") }}</a>
            </slot>
          </span>
        </template>
      </input-password>
    </div>
    
    <input
      class="button mt-4"
      type="submit"
      :value="$t('labels.login')"
      :disabled="state.loggingIn"
      @click="state.hideErrors = false"
    />

    <slot name="footer"></slot>
  </form>
</template>

<script setup lang="ts">
import { reactive, watch } from "vue";
import { useI18n } from "vue-i18n";
import InputText from "@core/input/InputText.vue";
import InputPassword from "@core/input/InputPassword.vue";

import { useUser } from "@/api/user.store";
import { showError } from "@core/ui/alert/alert";
import { GenericError, NoInternetError } from "@core/errors/Errors";
import { showUpdateNotificationIfNeeded } from "@core/functions/updateNotification";

import type { User } from "@/api/User";

const props = withDefaults(defineProps<{
  email?: string;
  shouldAutoloadData?: boolean;
}>(), {
  email: "",
  shouldAutoloadData: true
});

const emit = defineEmits<{
  (e: "login", user: User): void;
  (e: "update:email", value: string): void;
}>();

const { t } = useI18n();
const { getOrLoadUser, login } = useUser();
    
const data = reactive({
  emailValue: "",
  password: ""
});

const state = reactive({
  loggingIn: false,
  hideErrors: true,
  errors: {
    emailValue: "",
    password: ""
  }
});

watch(() => props.email, (value) => {
  data.emailValue = value;
}, { immediate: true });
      
watch(() => data.emailValue, (value) => {
  if (props.email != value) {
    emit("update:email", value);
  }
});
    
  
if (!props.email && props.shouldAutoloadData) {
  loadLastLoginData();
}
    
async function loadLastLoginData() {
  try {
    const user = await getOrLoadUser({ showLoginPopup: false });

    console.log("user", user);

    if (user.email) {
      data.emailValue = user.email;
      return;
    }
  } catch (error) {
    console.warn("couldnt load user", error);
    try {
      await restoreSavedLoginData();
    } catch (e) {
      console.warn("could not get user", e);
    }
  }
}

async function restoreSavedLoginData() {
  if (!window.electron) return null;

  const encryptedData = localStorage.getItem("login");
  if (!encryptedData) return null;

  const decryptedData = await window.electron.decryptCredential(
    encryptedData
  );
  if (!decryptedData) return null;

  const loginData = JSON.parse(decryptedData);
  if (!loginData) return null;

  if (loginData.email) data.emailValue = loginData.email;
  if (loginData.password) data.password = loginData.password;
}

async function onSubmit() {
  state.loggingIn = true;
  try {
    const user = await login(data.emailValue, data.password);

    showUpdateNotificationIfNeeded();

    emit("login", user);
  } catch (_error) {
    const error = _error as GenericError;
    state.hideErrors = false;
    console.error(error.status, error.message);

    if (error instanceof NoInternetError || error.status == null) {
      showError(t("errors.noInternet"));
    } else if (error.status === 400) {
      showError(t("errors.loginFailed"), {
        title: t("headings.loginFailed")
      });
    } else {
      showError(error);
    }
  } finally {
    state.loggingIn = false;
  }
}
</script>

<style lang="scss">
.login-form {
  // width: 20rem;
  width: 100%;
  max-width: 30rem;
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 0.5rem;
  text-align: left;

  &__title {
    text-align: center;
  }

  &__forgotpasswordlink {
    font-size: 12px;
    white-space: nowrap;
    padding-bottom: 0.25rem;
    a {
      cursor: pointer;
    }
  }
}
</style>
